import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ErrorBoundary from './ErrorBoundary';
import { isUrlInWhitelist } from '../../utils/imageWhitelist';

// 导入Mermaid和KaTeX
import mermaid from 'mermaid';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// 全局初始化Mermaid配置
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  },
  sequence: {
    useMaxWidth: true,
    diagramMarginX: 50,
    diagramMarginY: 10
  },
  gantt: {
    useMaxWidth: true
  },
  // 添加更多配置以提高兼容性
  pie: {
    useMaxWidth: true
  },
  journey: {
    useMaxWidth: true
  },
  class: {
    useMaxWidth: true
  },
  state: {
    useMaxWidth: true
  },
  er: {
    useMaxWidth: true
  },
  // 启用实验性功能
  experimental: {
    enableMultiplexer: true
  }
});

// 生成随机ID
const randomid = () => parseInt(String(Math.random() * 1e15), 10).toString(36);

// 防抖函数
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 安全的哈希函数，支持Unicode字符
const generateHash = (str) => {
  if (!str) return '';
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  
  // 转换为正数并转为16进制字符串
  return Math.abs(hash).toString(16).slice(0, 8);
};

// Mermaid图表渲染组件
const MermaidRenderer = ({ children = [], className, ...props }) => {
  const demoid = useRef(`dome${randomid()}`);
  const [container, setContainer] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState('');
  const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
  
  // 获取代码内容 - 确保是字符串类型
  const getCodeContent = () => {
    if (!children) return "";
    
    // 如果children是数组
    if (Array.isArray(children)) {
      // 遍历所有children，收集所有文本内容
      const allTexts = [];
      
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (typeof child === 'string') {
          allTexts.push(child);
        } else if (typeof child === 'number') {
          allTexts.push(String(child));
        } else if (child && typeof child === 'object' && child.props) {
          // 递归获取React元素的文本内容
          const extractText = (element) => {
            if (typeof element === 'string') {
              return element;
            }
            if (typeof element === 'number') {
              return String(element);
            }
            if (Array.isArray(element)) {
              return element.map(extractText).join('');
            }
            if (element && typeof element === 'object' && element.props) {
              return extractText(element.props.children);
            }
            return '';
          };
          const childText = extractText(child.props.children);
          if (childText) {
            allTexts.push(childText);
          }
        }
      }
      
      return allTexts.join('');
    }
    
    // 如果children是字符串
    if (typeof children === 'string') {
      return children;
    }
    
    // 如果children是React元素
    if (children && typeof children === 'object' && children.props) {
      // 递归获取所有文本内容
      const extractText = (element) => {
        if (typeof element === 'string') {
          return element;
        }
        if (typeof element === 'number') {
          return String(element);
        }
        if (Array.isArray(element)) {
          return element.map(extractText).join('');
        }
        if (element && typeof element === 'object' && element.props) {
          return extractText(element.props.children);
        }
        return '';
      };
      return extractText(children.props.children);
    }
    
    return "";
  };
  
  const code = getCodeContent();
  
  // 使用防抖减少频繁渲染
  const debouncedCode = useDebounce(code, 300);

  useEffect(() => {
    if (container && isMermaid && demoid.current && debouncedCode) {
      // 检查是否已经渲染过相同的内容
      const codeHash = generateHash(debouncedCode);
      const existingHash = container.getAttribute('data-code-hash');
      
      if (existingHash === codeHash && svgContent) {
        // 如果代码没有变化且已经渲染过，直接返回
        return;
      }
      
      try {
        // 预处理Mermaid代码，确保第一行包含图表类型声明
        let processedCode = debouncedCode.trim();
        

        
        // 如果代码不以图表类型开头，尝试自动检测
        if (!processedCode.match(/^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|gitgraph|C4Context|C4Container|C4Component|C4Code|C4Dynamic|C4Deployment|mindmap|timeline|zenuml|sankey|quadrantChart|requirement|gitgraph|user-journey|gitgraph|pie|quadrantChart|requirement|user-journey|sankey|timeline|zenuml)/i)) {
          // 尝试从代码中提取图表类型
          const lines = processedCode.split('\n').filter(line => line.trim());
          if (lines.length > 0) {
            const firstLine = lines[0].trim();
            // 如果第一行看起来像图表内容，添加默认的graph类型
            if (firstLine.includes('-->') || firstLine.includes('---') || firstLine.includes('==>')) {
              processedCode = `graph TD\n${processedCode}`;
            } else if (firstLine.includes('[') && firstLine.includes(']')) {
              processedCode = `graph TD\n${processedCode}`;
            } else {
              // 如果无法自动检测，强制添加默认类型
              processedCode = `graph TD\n${processedCode}`;
            }
          } else {
            // 如果代码为空，添加默认的示例
            processedCode = 'graph TD\nA[开始] --> B[结束]';
          }
        }
        

        
        // 最终验证：确保代码包含有效的图表类型
        if (!processedCode.match(/^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|gitgraph|C4Context|C4Container|C4Component|C4Code|C4Dynamic|C4Deployment|mindmap|timeline|zenuml|sankey|quadrantChart|requirement|gitgraph|user-journey|gitgraph|pie|quadrantChart|requirement|user-journey|sankey|timeline|zenuml)/i)) {
          console.error('Mermaid代码仍然没有有效的图表类型声明');
          setError('Mermaid代码格式错误：缺少图表类型声明');
          return;
        }
        
        // 渲染Mermaid图表
        mermaid.render(demoid.current, processedCode).then(({ svg, bindFunctions }) => {
          // 检查容器是否仍然存在
          if (container && container.parentNode) {
            try {
              container.innerHTML = svg;
              container.setAttribute('data-code-hash', codeHash);
              setSvgContent(svg);
              setError('');
              
              if (bindFunctions) {
                bindFunctions(container);
              }
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        }).catch((error) => {
          console.error('Mermaid渲染错误:', error);
          setError(error.message);
          if (container && container.parentNode) {
            try {
              container.innerHTML = `<div style="color: red;">Mermaid渲染失败: ${error.message}</div>`;
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        });
      } catch (err) {
        console.error('Mermaid初始化错误:', err);
        setError(err.message);
        if (container && container.parentNode) {
          try {
            container.innerHTML = `<div style="color: red;">Mermaid初始化失败: ${err.message}</div>`;
          } catch (domError) {
            console.error('DOM操作错误:', domError);
          }
        }
      }
    }
  }, [container, isMermaid, debouncedCode, demoid, svgContent]);

  const refElement = useCallback((node) => {
    if (node !== null && node !== container) {
      setContainer(node);
    } else if (node === null && container !== null) {
      setContainer(null);
    }
  }, [container]);

  // 清理函数
  useEffect(() => {
    return () => {
      if (container && container.parentNode) {
        try {
          container.innerHTML = '';
        } catch (error) {
          console.warn('清理容器时出错:', error);
        }
      }
    };
  }, [container]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      setSvgContent('');
      setError('');
      setContainer(null);
    };
  }, []);

  if (isMermaid) {
    return (
      <div className="my-4 text-center">
        <code id={demoid.current} className="hidden" />
        <div 
          ref={refElement} 
          data-name="mermaid"
          className="block h-75 border border-gray-300 rounded-lg p-5 bg-white shadow-sm overflow-auto"
        />

      </div>
    );
  }
  
  return <code className={className} {...props}>{children}</code>;
};

// KaTeX数学公式渲染组件
const MathRenderer = ({ code }) => {
  const [rendered, setRendered] = useState('');
  const [error, setError] = useState('');
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    if (code && !isRendering) {
      setIsRendering(true);
      
      try {
        // 渲染数学公式
        const html = katex.renderToString(code, {
          throwOnError: false,
          errorColor: '#cc0000',
          displayMode: true,
          output: 'html'
        });
        setRendered(html);
        setError('');
      } catch (err) {
        setError(`数学公式渲染失败: ${err.message}`);
        console.error('KaTeX渲染错误:', err);
      } finally {
        setIsRendering(false);
      }
    }
  }, [code, isRendering]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 my-2 text-red-600">
        <strong>数学公式渲染失败:</strong> {error}
      </div>
    );
  }

  if (isRendering) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded p-3 my-2 text-center text-gray-600">
        正在渲染数学公式...
      </div>
    );
  }

  return (
    <div 
      className="my-4 text-center overflow-auto p-5 bg-white border border-gray-300 rounded-lg shadow-sm h-25 flex items-center justify-center"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

const MarkdownRenderer = ({ content, className = '', onError, textColor }) => {
  const [markdownError, setMarkdownError] = useState(false);

  // 如果 Markdown 出错，显示原始文本
  if (markdownError) {
    return (
      <div
        className={`text-gray-700 leading-relaxed whitespace-pre-wrap ${className}`}
      >
        {content || '暂无内容'}
      </div>
    );
  }

  // 辅助函数：根据是否有 textColor 属性决定使用什么颜色类
  // 如果提供了 textColor，则使用该颜色，否则使用默认颜色
  const getColorClass = (defaultClass) => {
    return textColor ? textColor : defaultClass;
  };

  return (
    <div className={`markdown-content ${className} ${textColor || ''}`}>
      <ErrorBoundary onError={() => setMarkdownError(true)}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // 自定义标题样式
            h1: ({ children }) => (
              <h1 className={`text-2xl font-bold mb-4 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className={`text-xl font-semibold mb-3 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className={`text-lg font-semibold mb-2 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4 className={`text-base font-semibold mb-2 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h4>
            ),
            h5: ({ children }) => (
              <h5 className={`text-sm font-semibold mb-1 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h5>
            ),
            h6: ({ children }) => (
              <h6 className={`text-xs font-semibold mb-1 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </h6>
            ),
            // 段落样式
            p: ({ children }) => (
              <p className={`mb-3 leading-relaxed ${getColorClass('text-[var(--color-text-base)]')}`}>{children}</p>
            ),
            // 链接样式
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {children}
              </a>
            ),
            // 列表样式
            ul: ({ children }) => (
              <ul className={`list-disc list-inside mb-3 space-y-1 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className={`list-decimal list-inside mb-3 space-y-1 ${getColorClass('text-[var(--color-text-base)]')}`}>
                {children}
              </ol>
            ),
            li: ({ children }) => <li className={getColorClass('text-[var(--color-text-base)]')}>{children}</li>,
            // 代码样式 - 添加Mermaid和KaTeX支持
            code: ({ children, className, ...props }) => {
              const isInline = !className;
              const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
              const isMath = className && /^language-math/.test(className.toLowerCase());
              
              if (isMermaid) {
                return <MermaidRenderer children={children} className={className} {...props} />;
              }
              
              if (isMath) {
                return <MathRenderer code={children ? children[0] || "" : ""} />;
              }
              
              if (isInline) {
                return (
                  <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
                    {children}
                  </code>
                );
              }
              return (
                <code className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto">
                  {children}
                </code>
              );
            },
            pre: ({ children }) => (
              <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">
                {children}
              </pre>
            ),
            // 引用样式
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-3 bg-blue-50 text-gray-700 italic">
                {children}
              </blockquote>
            ),
            // 表格样式
            table: ({ children }) => (
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full border border-gray-300">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-gray-100">{children}</thead>
            ),
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => (
              <tr className="border-b border-gray-300">{children}</tr>
            ),
            th: ({ children }) => (
              <th className={`px-4 py-2 text-left font-semibold border-r border-gray-300 ${getColorClass('text-gray-900')}`}>
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className={`px-4 py-2 border-r border-gray-300 ${getColorClass('text-gray-700')}`}>
                {children}
              </td>
            ),
            // 分割线样式
            hr: () => <hr className="border-t border-gray-300 my-4" />,
            // 强调样式
            strong: ({ children }) => (
              <strong className={`font-semibold ${getColorClass('text-gray-900')}`}>
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className={`italic ${getColorClass('text-gray-800')}`}>{children}</em>
            ),
            // 删除线样式
            del: ({ children }) => (
              <del className="line-through text-gray-500">{children}</del>
            ),
            // 图片样式 - 添加白名单验证
            img: ({ src, alt }) => {
              // 验证图片URL是否在白名单中
              if (!isUrlInWhitelist(src)) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-4 w-4 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                      </div>
                      <div className="ml-2">
                        <span className="text-sm font-medium text-red-800">
                          不允许上传外部图片
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <img
                  src={src}
                  alt={alt}
                  className="max-w-full h-auto rounded mb-3"
                  style={{ maxHeight: '400px' }}
                />
              );
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </ErrorBoundary>
    </div>
  );
};

export default MarkdownRenderer;
